import 'project/polyfill'
import '../styles/Global.scss'
import Head from 'next/head'
import { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import { FC, useEffect, useState } from 'react'

import { NextPageWithLayout } from 'types/nextPageWithLayout'
import AnalyticsContainer from 'components/util/containers/AnalyticsContainer'
import PageContainer from 'components/util/containers/PageContainer'
import LocaleContainer from 'components/util/containers/LocaleContainer'
import { BagItem, ShoppingBagContext } from 'common/useShoppingBag'
import { API } from 'project/api'
import { Project } from 'common/project'
import runWhenIdle from 'common/runWhenIdle'
import NProgress from 'components/util/NProgress'
import ScrollOnRoute from 'components/util/ScrollOnRoute'
import { shouldDisableTracking } from 'project/isWithoutNav'
import ToltCouponProvider from 'components/ToltCouponProvider'
import useCookieRedirect, { readCookieData } from 'project/useCookieRedirect'

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
  pageProps: {}
}
require('public/images/plans/internation-page-plugz-head-desktop.png').default

readCookieData()
let initialBag: BagItem = { addons: [], duration: 'month', slug: null }
try {
  initialBag = JSON.parse(API.storage.getItemSync!('bag'))
  if (!initialBag.addons) {
    throw 'Invalid bag detected'
  }
} catch (e) {}

const AppComponent: FC<AppPropsWithLayout> = ({ Component, pageProps }) => {
  useCookieRedirect()
  const [bag, setBag] = useState<BagItem>(initialBag)

  useEffect(() => {
    if (bag) {
      API.storage.setItemSync!('bag', JSON.stringify(bag))
    }
  }, [bag])

  useEffect(() => {
    if (shouldDisableTracking()) {
      return
    }

    // Load GTM script
    if (Project.gtm) {
      runWhenIdle(() => {
        const gtmScript = document.createElement('script')
        gtmScript.defer = true
        gtmScript.innerHTML = `!function(){"use strict";function l(e){for(var t=e,r=0,n=document.cookie.split(";");r<n.length;r++){var o=n[r].split("=");if(o[0].trim()===t)return o[1]}}function s(e){return localStorage.getItem(e)}function u(e){return window[e]}function A(e,t){e=document.querySelector(e);return t?null==e?void 0:e.getAttribute(t):null==e?void 0:e.textContent}var e=window,t=document,r="script",n="dataLayer",o="https://analytics.hoxtonmix.com",a="",i="anbthremgh",c="5d4stk=aWQ9R1RNLUs2MzZaVw%3D%3D&sort=asc",g="cookie",v="stape",E="",d=!1;try{var d=!!g&&(m=navigator.userAgent,!!(m=new RegExp("Version/([0-9._]+)(.*Mobile)?.*Safari.*").exec(m)))&&16.4<=parseFloat(m[1]),f="stapeUserId"===g,I=d&&!f?function(e,t,r){void 0===t&&(t="");var n={cookie:l,localStorage:s,jsVariable:u,cssSelector:A},t=Array.isArray(t)?t:[t];if(e&&n[e])for(var o=n[e],a=0,i=t;a<i.length;a++){var c=i[a],c=r?o(c,r):o(c);if(c)return c}else console.warn("invalid uid source",e)}(g,v,E):void 0;d=d&&(!!I||f)}catch(e){console.error(e)}var m=e,g=(m[n]=m[n]||[],m[n].push({"gtm.start":(new Date).getTime(),event:"gtm.js"}),t.getElementsByTagName(r)[0]),v=I?"&bi="+encodeURIComponent(I):"",E=t.createElement(r),f=(d&&(i=8<i.length?i.replace(/([a-z]{8}$)/,"kp$1"):"kp"+i),!d&&a?a:o);E.async=!0,E.src=f+"/"+i+".js?"+c+v,null!=(e=g.parentNode)&&e.insertBefore(E,g)}();`
        document.head.appendChild(gtmScript)

        // Add GTM noscript fallback
        const noscript = document.createElement('noscript')
        noscript.innerHTML = `<iframe src="https://analytics.hoxtonmix.com/ns.html?id=GTM-K636ZW" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
        document.body.appendChild(noscript)
      })
    }

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

    if (isSafari) {
      document.body.classList.add('safari')
    }
  }, [])

  const getLayout =
    Component.getLayout || ((page) => <PageContainer>{page}</PageContainer>)
  const router = useRouter()

  if (router.asPath.includes('sw.js')) {
    return null
  }

  return (
    <ShoppingBagContext.Provider
      value={{
        setValue: setBag,
        value: bag,
      }}
    >
      <LocaleContainer>
        <AnalyticsContainer Component={Component} />
        <Head>
          {!process.env.prod && (
            <meta name='robots' content='noindex, nofollow' />
          )}
          <meta charSet='utf-8' />
          <meta httpEquiv='x-ua-compatible' content='ie=edge' />
          <meta name='theme-color' content='#ffffff' />
          <meta
            name='viewport'
            content='width=device-width, initial-scale=1.0, maximum-scale=2.0'
          />
          <meta property='fb:admins' content='453323778023845' />
          <link
            rel='icon'
            type='image/png'
            href='/favicon-96x96.png'
            sizes='96x96'
          />
          <link rel='icon' type='image/svg+xml' href='/favicon.svg' />
          <link rel='shortcut icon' href='/favicon.ico' />
          <link
            rel='apple-touch-icon'
            sizes='180x180'
            href='/apple-touch-icon.png'
          />

          <link rel='manifest' href='/site.webmanifest' />
          <meta name='theme-color' content='#ffffff' />
          <link rel='manifest' href='/manifest.webmanifest' />
          <link
            rel='preload'
            as='font'
            type='font/woff2'
            href='/fonts/open-sans-v17-latin-regular.woff2'
            crossOrigin={'crossorigin'}
          />
          <link
            rel='preload'
            as='font'
            type='font/woff2'
            href='/fonts/open-sans-v17-latin-600.woff2'
            crossOrigin={'crossorigin'}
          />
          <link
            rel='preload'
            as='font'
            type='font/woff2'
            href='/fonts/open-sans-v17-latin-700.woff2'
            crossOrigin={'crossorigin'}
          />
          <link rel='dns-prefetch' href='https://images.prismic.io' />
          <link rel='dns-prefetch' href='https://imgproxy.hoxtonmix.com' />
          <link rel='dns-prefetch' href='https://analytics.hoxtonmix.com' />
        </Head>

        <ToltCouponProvider />
        <NProgress />
        {getLayout(
          // @ts-ignore
          <Component {...pageProps} />,
          pageProps,
        )}
      </LocaleContainer>

      <ScrollOnRoute />
    </ShoppingBagContext.Provider>
  )
}
export default AppComponent
